import React from 'react';
import theme from '../styles/theme';

// Button Component with several variants
export const Button = ({ 
  children, 
  variant = 'primary', 
  size = 'medium',
  disabled = false,
  onClick,
  className = '',
  ...props 
}) => {
  // Get button style based on variant
  const buttonStyle = theme.buttons[variant] || theme.buttons.primary;
  
  // Size maps
  const sizeMap = {
    small: {
      padding: `${theme.spacing.xs} ${theme.spacing.md}`,
      fontSize: '0.875rem',
    },
    medium: {
      padding: `${theme.spacing.sm} ${theme.spacing.lg}`,
      fontSize: '1rem',
    },
    large: {
      padding: `${theme.spacing.md} ${theme.spacing.xl}`,
      fontSize: '1.125rem',
    },
  };
  
  const sizeStyle = sizeMap[size] || sizeMap.medium;
  
  // Base style
  const style = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: buttonStyle.backgroundColor,
    color: buttonStyle.color,
    border: buttonStyle.border || 'none',
    borderRadius: theme.borders.radius.md,
    fontFamily: theme.typography.fontFamily,
    fontWeight: 600,
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: `all ${theme.animation.durations.normal} ${theme.animation.easing.easeOut}`,
    boxShadow: theme.borders.shadow.small,
    opacity: disabled ? 0.6 : 1,
    ...sizeStyle,
    ...props.style,
  };
  
  // Hover effect handler
  const [isHovered, setIsHovered] = React.useState(false);
  const [isActive, setIsActive] = React.useState(false);
  
  // Update style based on hover/active state
  if (isHovered && !disabled) {
    style.backgroundColor = buttonStyle.hoverBackgroundColor;
    style.boxShadow = theme.borders.shadow.medium;
  }
  
  if (isActive && !disabled) {
    style.backgroundColor = buttonStyle.activeBackgroundColor;
    style.boxShadow = 'none';
    style.transform = 'translateY(1px)';
  }
  
  return (
    <button
      style={style}
      onClick={disabled ? undefined : onClick}
      className={className}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => {
        setIsHovered(false);
        setIsActive(false);
      }}
      onMouseDown={() => setIsActive(true)}
      onMouseUp={() => setIsActive(false)}
      disabled={disabled}
      {...props}
    >
      {children}
    </button>
  );
};

// Card component for panel backgrounds
export const Card = ({ 
  children, 
  elevation = 'medium',
  padding = 'medium',
  className = '',
  ...props 
}) => {
  // Shadow maps
  const shadowMap = {
    small: theme.borders.shadow.small,
    medium: theme.borders.shadow.medium,
    large: theme.borders.shadow.large,
    glow: theme.borders.shadow.glow,
  };
  
  // Padding maps
  const paddingMap = {
    small: theme.spacing.md,
    medium: theme.spacing.lg,
    large: theme.spacing.xl,
  };
  
  const style = {
    backgroundColor: theme.colors.card,
    color: theme.colors.textPrimary,
    borderRadius: theme.borders.radius.lg,
    boxShadow: shadowMap[elevation] || shadowMap.medium,
    padding: paddingMap[padding] || paddingMap.medium,
    ...props.style,
  };
  
  return (
    <div 
      style={style} 
      className={className}
      {...props}
    >
      {children}
    </div>
  );
};

// Status badge for showing player info or states
export const Badge = ({ 
  children, 
  color = 'primary',
  size = 'medium',
  ...props 
}) => {
  // Color maps to theme colors
  const getColor = (colorName) => {
    return theme.colors[colorName] || colorName;
  };
  
  // Size maps
  const sizeMap = {
    small: {
      padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
      fontSize: '0.75rem',
    },
    medium: {
      padding: `${theme.spacing.xs} ${theme.spacing.md}`,
      fontSize: '0.875rem',
    },
    large: {
      padding: `${theme.spacing.sm} ${theme.spacing.md}`,
      fontSize: '1rem',
    },
  };
  
  const style = {
    display: 'inline-block',
    backgroundColor: getColor(color),
    color: '#fff',
    borderRadius: theme.borders.radius.round,
    fontWeight: 600,
    ...sizeMap[size],
    ...props.style,
  };
  
  return (
    <span 
      style={style}
      {...props}
    >
      {children}
    </span>
  );
};

// Typography components
export const Title = ({ 
  children, 
  level = 1,
  color = 'textPrimary',
  align = 'left',
  ...props 
}) => {
  const fontSize = {
    1: '2.5rem',
    2: '2rem',
    3: '1.75rem',
    4: '1.5rem',
    5: '1.25rem',
    6: '1rem',
  }[level] || '1rem';
  
  const Tag = `h${level}`;
  
  const style = {
    color: theme.colors[color] || color,
    fontFamily: theme.typography.fontFamily,
    fontWeight: theme.typography.heading.fontWeight,
    lineHeight: theme.typography.heading.lineHeight,
    margin: `${theme.spacing.md} 0`,
    textAlign: align,
    fontSize,
    ...props.style,
  };
  
  return (
    <Tag 
      style={style}
      {...props}
    >
      {children}
    </Tag>
  );
};

export const Text = ({ 
  children, 
  size = 'medium',
  color = 'textPrimary',
  align = 'left',
  bold = false,
  ...props 
}) => {
  // Size maps
  const sizeMap = {
    small: '0.875rem',
    medium: '1rem',
    large: '1.125rem',
    xl: '1.25rem',
  };
  
  const style = {
    color: theme.colors[color] || color,
    fontFamily: theme.typography.fontFamily,
    fontWeight: bold ? 600 : theme.typography.body.fontWeight,
    lineHeight: theme.typography.body.lineHeight,
    fontSize: sizeMap[size] || sizeMap.medium,
    textAlign: align,
    margin: `${theme.spacing.sm} 0`,
    ...props.style,
  };
  
  return (
    <p 
      style={style}
      {...props}
    >
      {children}
    </p>
  );
};

// Flex container for layout
export const Flex = ({ 
  children, 
  direction = 'row',
  justify = 'flex-start',
  align = 'stretch',
  wrap = 'nowrap',
  gap = 'md',
  ...props 
}) => {
  // Gap map
  const gapSize = theme.spacing[gap] || gap;
  
  const style = {
    display: 'flex',
    flexDirection: direction,
    justifyContent: justify,
    alignItems: align,
    flexWrap: wrap,
    gap: gapSize,
    ...props.style,
  };
  
  return (
    <div 
      style={style}
      {...props}
    >
      {children}
    </div>
  );
};

// Grid container
export const Grid = ({ 
  children, 
  columns = 'repeat(auto-fit, minmax(250px, 1fr))',
  gap = 'md',
  ...props 
}) => {
  // Gap map
  const gapSize = theme.spacing[gap] || gap;
  
  const style = {
    display: 'grid',
    gridTemplateColumns: columns,
    gap: gapSize,
    ...props.style,
  };
  
  return (
    <div 
      style={style}
      {...props}
    >
      {children}
    </div>
  );
};

// Progress bar for indicating player scores
export const ProgressBar = ({
  value,
  max = 100,
  color = 'primary',
  height = '8px',
  ...props
}) => {
  const percentage = Math.min(100, Math.max(0, (value / max) * 100));
  
  const containerStyle = {
    width: '100%',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: theme.borders.radius.sm,
    height,
    overflow: 'hidden',
    ...props.style,
  };
  
  const barStyle = {
    width: `${percentage}%`,
    height: '100%',
    backgroundColor: theme.colors[color] || color,
    borderRadius: theme.borders.radius.sm,
    transition: `width ${theme.animation.durations.normal} ${theme.animation.easing.easeOut}`,
  };
  
  return (
    <div style={containerStyle}>
      <div style={barStyle} />
    </div>
  );
};

// Container component for page layout
export const Container = ({
  children,
  maxWidth = 'lg',
  padding = true,
  fullHeight = false,
  ...props
}) => {
  const maxWidthMap = {
    sm: '600px',
    md: '960px',
    lg: '1200px',
    xl: '1440px',
    full: '100%',
  };
  
  const style = {
    width: '100%',
    maxWidth: maxWidthMap[maxWidth] || maxWidth,
    marginLeft: 'auto',
    marginRight: 'auto',
    padding: padding ? theme.spacing.lg : 0,
    height: fullHeight ? '100vh' : 'auto',
    display: fullHeight ? 'flex' : 'block',
    flexDirection: fullHeight ? 'column' : 'initial',
    ...props.style,
  };
  
  return (
    <div 
      style={style}
      {...props}
    >
      {children}
    </div>
  );
}; 